import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  HStack,
  Rating,
  theme,
  TitleText,
  usePressableState,
  useResponsive,
} from '@sweetgreen/garnish';

import { PageWrapper } from '@order/components';

// ─────────────────────────────────────────────────────────────────────────────

export const RatingContent = React.memo((props: RatingContentProps) => {
  const {
    ratingTitle,
    ratingSubtitle,
    ratingHint,
    ratingCta,
    cards,
    rating,
    submitting,
    onRating,
    onHoverRating,
    onSelectCard,
    onSubmit,
  } = props;

  const { match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerStyles = [
    styles.contentContainer,
    match([
      styles.contentContainerXS,
      styles.contentContainerSM,
      styles.contentContainerMD,
    ]),
  ];
  const ratingCardsContainerStyles = [
    styles.ratingCardsContainer,
    match([undefined, styles.ratingCardsContainerSM]),
  ];
  const ctaContainerStyles = [
    styles.ctaContainer,
    match([undefined, styles.ctaContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <PageWrapper
      withoutFooter
      withHeaderTitle
      contentContainerStyle={styles.pageWrapper}
      bounces={false}
    >
      <View style={contentContainerStyles}>
        <Rating value={rating} onChange={onRating} onHover={onHoverRating} />

        <BodyText style={styles.ratingTitle}>{ratingTitle}</BodyText>

        <TitleText size={5} style={styles.ratingSubtitle}>
          {ratingSubtitle}
        </TitleText>

        <BodyText size={5} style={styles.ratingHint}>
          {ratingHint}
        </BodyText>

        <ScrollView style={ratingCardsContainerStyles} bounces={false}>
          <HStack
            itemsPerRow={match([2, 3])}
            customSpacing={theme.spacing.medium}
          >
            {cards.map((card) => (
              <RatingCardComponent
                key={card.id}
                {...card}
                onSelectCard={onSelectCard}
              />
            ))}
          </HStack>
        </ScrollView>

        <View style={ctaContainerStyles}>
          <Button
            testID="rate-order.submit"
            width="100%"
            accessibilityRole="button"
            accessibilityLabel={ratingCta}
            size="large"
            isLoading={submitting}
            onPress={onSubmit}
          >
            {ratingCta}
          </Button>
        </View>
      </View>
    </PageWrapper>
  );
});

// ─── Subcomponents ───────────────────────────────────────────────────────────

const RatingCardComponent = ({
  id,
  label,
  selected,
  onSelectCard,
}: RatingCardProps) => {
  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);
  const handleOnPress = React.useCallback(() => {
    onSelectCard(id, !selected);
  }, [id, selected, onSelectCard]);

  return (
    <Pressable
      ref={ref}
      testID={`rate-order.card.${id}`}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={handleOnPress}
      style={[
        styles.card,
        selected ? styles.cardSelected : isInteracting && styles.cardHovered,
      ]}
    >
      <BodyText size={4} style={styles.cardLabel}>
        {label}
      </BodyText>
    </Pressable>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RatingContentProps = {
  readonly ratingTitle: string;
  readonly ratingSubtitle: string;
  readonly ratingHint: string;
  readonly ratingCta: string;
  readonly cards: readonly RatingCard[];
  readonly rating: number;
  readonly submitting: boolean;
  readonly onHoverRating: (rating: number) => void;
  readonly onRating: (rating: number) => void;
  readonly onSelectCard: (id: string, selected: boolean) => void;
  readonly onSubmit: () => void;
};

type RatingCardProps = {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelectCard: (id: string, selected: boolean) => void;
};

type RatingCard = {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    maxHeight: '100%',
  },
  contentContainer: {
    flex: 1,
    alignItems: 'center',
  },
  contentContainerXS: {
    paddingTop: theme.spacing.xxlarge,
    paddingHorizontal: theme.spacing.medium,
    paddingBottom: theme.spacing.large,
  },
  contentContainerSM: {
    paddingTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.large,
    paddingBottom: theme.spacing.xlarge,
  },
  contentContainerMD: {
    paddingTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.xlarge,
    paddingBottom: theme.spacing.xlarge,
  },
  ratingCardsContainer: {
    flex: 1,
  },
  ratingCardsContainerSM: {
    minHeight: 240,
  },
  ratingTitle: {
    marginTop: theme.spacing.medium,
    color: theme.colors.KALE,
  },
  ratingSubtitle: {
    marginTop: theme.spacing.large,
  },
  ratingHint: {
    color: theme.colors.CHARCOAL,
    marginVertical: theme.spacing.large,
  },
  card: {
    minHeight: 64,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.OPACITY.GREEN_1.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.xxlarge,
    paddingVertical: theme.spacing.small,
    paddingHorizontal: theme.spacing.medium,
  },
  cardLabel: {
    textAlign: 'center',
  },
  cardHovered: {
    backgroundColor: theme.colors.OPACITY.GREEN_2.LIGHTEST,
  },
  cardSelected: {
    backgroundColor: theme.colors.OPACITY.GREEN_3.LIGHT,
    borderWidth: 1,
    borderColor: theme.colors.GREEN_2,
  },
  ctaContainer: {
    width: '100%',
    marginTop: theme.spacing.large,
  },
  ctaContainerSM: {
    maxWidth: 400,
  },
});
